import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { DialogService } from './dialog.service';
import { ToothDataNew } from '../models/order.models';

@Injectable()  // component-scoped — add to providers: [] in the component decorator
export class MaterialService {

  newMaterial = '';
  materialList: string[] = [];
  materialParams: any[] = [];
  editIndex: number | null = null;
  oldValue = '';
  selectedPropName = '';
  expandedRowData: any = {};
  newMaterialParams: any[] = [{ name: '', min: null, max: null, step: null, default: null }];
  showDropdown: boolean[] = [];
  filteredOptions: string[][] = [];
  showDropdownEdit: boolean[][] = [];
  filteredOptionsEdit: string[][][] = [];

  readonly paramOptions = ['minimalThickness', 'cementGap', 'marginGap', 'horizontalBorder', 'radiusCompensation'];

  constructor(private dataService: DataService, private dialogService: DialogService) {}

  resetMaterialState(): void {
    this.newMaterial = '';
    this.newMaterialParams = [{ name: '', min: null, max: null, step: null, default: null }];
    this.editIndex = null;
    this.oldValue = '';
    this.expandedRowData = [];
    this.showDropdown = [];
    this.filteredOptions = [];
    this.showDropdownEdit = [];
    this.filteredOptionsEdit = [];
  }

  addNewMaterial(
    currentProps: any[],
    teethType: string,
    currentToothData: any,
    onPropsUpdated: (props: any) => void,
    onRefresh: () => void
  ): void {
    if (!this.newMaterial || this.newMaterial.trim() === '' || this.newMaterial.trim() === 'Add New...') {
      this.dialogService.openMessageDialog('Error', 'Please enter material name.');
      return;
    }

    const materialProp = currentProps.find((p: any) => p.name === 'Material');
    let existingMaterials: string[] = [];
    if (Array.isArray(materialProp?.values)) {
      existingMaterials = materialProp.values;
    } else if (typeof materialProp?.values === 'string') {
      existingMaterials = materialProp.values.split(',').map((v: string) => v.trim());
    }

    if (existingMaterials.some((m: string) => m.toLowerCase() === this.newMaterial.toLowerCase())) {
      this.dialogService.openMessageDialog('Error', 'Material already exists.');
      return;
    }

    if (!this.newMaterialParams || this.newMaterialParams.length === 0) {
      this.dialogService.openMessageDialog('Error', 'At least one parameter is required.');
      return;
    }

    const hasAtLeastOneFilledRow = this.newMaterialParams.some(p =>
      p.name && p.name.trim() !== '' &&
      p.min != null && p.max != null && p.step != null && p.default != null
    );
    if (!hasAtLeastOneFilledRow) {
      this.dialogService.openMessageDialog('Error', 'Please fill at least one parameter row.');
      return;
    }

    for (let i = 0; i < this.newMaterialParams.length; i++) {
      const p = this.newMaterialParams[i];
      if (!p.name || p.name.trim() === '') {
        this.dialogService.openMessageDialog('Error', `Parameter name is required (row ${i + 1})`);
        return;
      }
      if (p.min == null || p.max == null || p.step == null || p.default == null) {
        this.dialogService.openMessageDialog('Error', `All fields are required (row ${i + 1})`);
        return;
      }
      if (p.min > p.max) {
        this.dialogService.openMessageDialog('Error', `Min cannot be greater than Max (row ${i + 1})`);
        return;
      }
      if (p.default < p.min || p.default > p.max) {
        this.dialogService.openMessageDialog('Error', `Default must be between Min and Max (row ${i + 1})`);
        return;
      }
      if (p.step <= 0) {
        this.dialogService.openMessageDialog('Error', `Step must be greater than 0 (row ${i + 1})`);
        return;
      }
    }

    const hasEmptyRow = this.newMaterialParams.some(p =>
      !p.name || p.min == null || p.max == null || p.step == null || p.default == null
    );
    const paramNames = this.newMaterialParams.map(p => p.name?.trim().toLowerCase());
    const hasDuplicateParams = paramNames.some((name, index) => name && paramNames.indexOf(name) !== index);

    if (hasDuplicateParams) {
      this.dialogService.openMessageDialog('Error', 'Parameter names must be unique.');
      return;
    }
    if (hasEmptyRow) {
      this.dialogService.openMessageDialog('Error', 'Please fill all parameter fields.');
      return;
    }

    const parameters = this.newMaterialParams.map(p => ({
      name: p.name, min: p.min, max: p.max, step: p.step, default: p.default
    }));
    const payload = { propName: 'Material', valueToAdd: this.newMaterial, teethType, parameters };

    this.dataService.updateProgressBarSubject(true);
    this.dataService.post('/tasks/props', payload).subscribe({
      next: (response: any) => {
        if (currentToothData) {
          currentToothData.props['Material'] = this.newMaterial;
        }
        this.materialList.push(this.newMaterial);
        onPropsUpdated(response);
        onRefresh();
        this.dataService.updateProgressBarSubject(false);
        this.newMaterial = '';
        this.newMaterialParams = [{ name: '', min: null, max: null, step: null, default: null }];
        this.dialogService.openMessageDialog('Info', `Item added successfully.`);
      }
    });
  }

  loadMaterialParams(material: string, teethType: string): void {
    this.dataService
      .get(`/tasks/getSingleProps/props?parent=${teethType}&name=Material&value=${material}`)
      .subscribe({
        next: (res: any) => {
          const data = res[0];
          this.materialParams = (data.parameters || []).map((p: any) => ({
            name: p.name, min: p.min, max: p.max, step: p.step, value: p.default
          }));
        }
      });
  }

  loadMaterialParamsForXmlUpload(material: string, teethType: string): void {
    this.dataService
      .get(`/tasks/getSingleProps/props?parent=${teethType}&name=Material&value=${material}`)
      .subscribe({
        next: (res: any) => {
          const data = res[0];
          this.materialParams = (data.parameters || []).map((p: any) => ({
            name: p.name, min: p.min, max: p.max, step: p.step, value: p.default
          }));
        }
      });
  }

  removeMaterial(
    index: number,
    teethType: string,
    onPropsUpdated: (props: any) => void,
    onRefresh: () => void
  ): void {
    const valueToRemove = this.materialList[index];
    this.dataService.delete(`/tasks/props/Material?parent=${teethType}&valueToRemove=${valueToRemove}`).subscribe({
      next: (response: any) => {
        this.materialList.splice(index, 1);
        onPropsUpdated(response);
        onRefresh();
        this.dialogService.openMessageDialog('Info', `${valueToRemove} deleted successfully.`);
      },
      error: (err: any) => {
        this.dialogService.handleError(err, 'Failed to Delete, Please try again.');
      }
    });
  }

  startEdit(index: number, item: string, teethType: string): void {
    this.editIndex = index;
    this.dataService.get(`/tasks/getSingleProps/props?parent=${teethType}&name=Material&value=${item}`)
      .subscribe({
        next: (res: any) => {
          if (res && res[0]) {
            const data = res[0];
            if (data.parameters) {
              data.parameters = data.parameters.map((p: any) => ({ ...p, showEditList: false }));
            }
            this.expandedRowData[index] = data;
          }
        }
      });
  }

  addTempParam(): void {
    this.newMaterialParams.push({ name: '', min: null, max: null, step: null, default: null });
  }

  removeTempParam(index: number): void {
    this.newMaterialParams.splice(index, 1);
  }

  saveEdit(
    index: number,
    valueFromInput: string,
    currentProps: any[],
    teethType: string,
    onPropsUpdated: (props: any) => void,
    onRefresh: () => void
  ): void {
    const newValue = valueFromInput.trim();
    const oldValue = this.oldValue;

    if (!newValue) {
      this.dialogService.openMessageDialog('Error', 'Material name is required.');
      return;
    }
    if (newValue === oldValue) {
      this.editIndex = null;
      return;
    }

    const materialProp = currentProps.find((p: any) => p.name === 'Material');
    let existingMaterials: string[] = [];
    if (Array.isArray(materialProp?.values)) {
      existingMaterials = materialProp.values;
    } else if (typeof materialProp?.values === 'string') {
      existingMaterials = materialProp.values.split(',').map((v: string) => v.trim());
    }

    const isDuplicate = existingMaterials.filter((_, i) => i !== index)
      .some((m: string) => m.toLowerCase() === newValue.toLowerCase());
    if (isDuplicate) {
      this.dialogService.openMessageDialog('Error', 'Material already exists.');
      return;
    }

    const parameters = this.expandedRowData[index]?.parameters || [];
    if (!parameters || parameters.length === 0) {
      this.dialogService.openMessageDialog('Error', 'At least one parameter is required.');
      return;
    }

    const hasAtLeastOneFilledRow = parameters.some((p: any) =>
      p.name && p.name.trim() !== '' &&
      p.min != null && p.max != null && p.step != null && p.default != null
    );
    if (!hasAtLeastOneFilledRow) {
      this.dialogService.openMessageDialog('Error', 'Please fill at least one parameter row.');
      return;
    }

    for (let i = 0; i < parameters.length; i++) {
      const p = parameters[i];
      if (!p.name || p.name.trim() === '') {
        this.dialogService.openMessageDialog('Error', `Parameter name required (row ${i + 1})`);
        return;
      }
      if (p.min == null || p.max == null || p.step == null || p.default == null) {
        this.dialogService.openMessageDialog('Error', `All fields required (row ${i + 1})`);
        return;
      }
      if (p.min > p.max) {
        this.dialogService.openMessageDialog('Error', `Min > Max (row ${i + 1})`);
        return;
      }
      if (p.default < p.min || p.default > p.max) {
        this.dialogService.openMessageDialog('Error', `Default must be between Min & Max (row ${i + 1})`);
        return;
      }
      if (p.step <= 0) {
        this.dialogService.openMessageDialog('Error', `Step must be > 0 (row ${i + 1})`);
        return;
      }
    }

    const paramNames = parameters.map((p: any) => p.name?.trim().toLowerCase());
    if (paramNames.some((name: any, i: any) => name && paramNames.indexOf(name) !== i)) {
      this.dialogService.openMessageDialog('Error', 'Parameter names must be unique.');
      return;
    }

    this.dataService.put(`/tasks/props/Material`, { oldValue, newValue, teethType, parameters }).subscribe({
      next: (response: any) => {
        this.materialList[index] = newValue;
        onPropsUpdated(response);
        this.editIndex = null;
        this.oldValue = '';
        this.dialogService.openMessageDialog('Info', `Item updated successfully.`);
        onRefresh();
      },
      error: () => {
        this.dialogService.openMessageDialog('Error', 'Update failed. Please try again.');
      }
    });
  }

  setDefaultMaterial(
    currentProps: any[],
    toothData: ToothDataNew[],
    teethType: string,
    onDefaultSet: (value: string) => void
  ): void {
    const materialProp = currentProps.find(p => p.name === 'Material');
    if (materialProp) {
      const firstValue = materialProp.values.split(',')[0];
      onDefaultSet(firstValue);
      this.loadMaterialParams(firstValue, teethType);
      toothData.filter(d => d.selected).forEach(d => { d.props['Material'] = firstValue; });
    }
  }

  setDefaultMaterialForUploadXmlFile(
    extractedValue: string,
    currentProps: any[],
    toothData: ToothDataNew[],
    teethType: string,
    onDefaultSet: (value: string) => void
  ): void {
    const materialProp = currentProps.find(p => p.name === 'Material');
    if (materialProp) {
      const availableValues = materialProp.values.split(',');
      const matchedValue = availableValues.find((v: string) =>
        v.trim().toLowerCase().includes(extractedValue.toLowerCase())
      );
      const finalValue = matchedValue ? matchedValue : availableValues[0];
      onDefaultSet(finalValue);
      this.loadMaterialParams(finalValue, teethType);
      toothData.filter(d => d.selected).forEach(d => { d.props['Material'] = finalValue; });
    }
  }

  filterOptions(index: number): void {
    const value = this.newMaterialParams[index].name?.toLowerCase() || '';
    this.filteredOptions[index] = this.paramOptions.filter(opt => opt.toLowerCase().includes(value));
  }

  selectOption(value: string, index: number): void {
    this.newMaterialParams[index].name = value;
    this.showDropdown[index] = false;
  }

  hideDropdown(index: number): void {
    setTimeout(() => { this.showDropdown[index] = false; }, 200);
  }

  addEditParam(index: number): void {
    if (!this.expandedRowData[index].parameters) this.expandedRowData[index].parameters = [];
    this.expandedRowData[index].parameters.push({ name: '', min: null, max: null, step: null, default: null });
  }

  removeEditParam(rowIndex: number, paramIndex: number): void {
    this.expandedRowData[rowIndex].parameters.splice(paramIndex, 1);
  }

  selectEditOption(value: string, param: any): void {
    param.name = value;
    param.showEditList = false;
  }

  hideEditDropdown(param: any): void {
    setTimeout(() => { param.showEditList = false; }, 200);
  }

  openDropdown(i: number, j: number): void {
    this.showDropdown[i] = true;
    this.filteredOptions[i] = [...this.paramOptions];
    if (!this.filteredOptions[i]) this.filteredOptions[i] = [];
    if (!this.showDropdownEdit[i]) this.showDropdownEdit[i] = [];
    if (!this.filteredOptionsEdit[i]) this.filteredOptionsEdit[i] = [];
    this.showDropdownEdit[i][j] = true;
    this.filteredOptionsEdit[i][j] = [...this.paramOptions];
  }

  openDropdownEdit(i: number, j: number): void {
    if (!this.showDropdownEdit[i]) this.showDropdownEdit[i] = [];
    if (!this.filteredOptionsEdit[i]) this.filteredOptionsEdit[i] = [];
    this.showDropdownEdit[i][j] = true;
    this.filteredOptionsEdit[i][j] = [...this.paramOptions];
  }

  filterOptionsEdit(i: number, j: number): void {
    const value = this.expandedRowData[i].parameters[j].name?.toLowerCase() || '';
    if (!this.filteredOptionsEdit[i]) this.filteredOptionsEdit[i] = [];
    this.filteredOptionsEdit[i][j] = this.paramOptions.filter(opt => opt.toLowerCase().includes(value));
    if (!this.showDropdownEdit[i]) this.showDropdownEdit[i] = [];
    this.showDropdownEdit[i][j] = true;
  }

  selectOptionEdit(option: string, i: number, j: number): void {
    this.expandedRowData[i].parameters[j].name = option;
    if (!this.showDropdownEdit[i]) this.showDropdownEdit[i] = [];
    this.showDropdownEdit[i][j] = false;
  }

  hideDropdownEdit(i: number, j: number): void {
    setTimeout(() => {
      if (this.showDropdownEdit[i]) this.showDropdownEdit[i][j] = false;
    }, 200);
  }

  formatParamName(name: string): string {
    if (!name) return '';
    return name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  }
}
